import { useId, type InputHTMLAttributes } from 'react';

/**
 * Every form field looks the same (v0.5, Ivan: the loan form's date was a different size): 44px
 * tall, full width, radius 16, 16px padding. Native date inputs (iOS Safari, Android Chrome) get
 * appearance none and a left-aligned value so they size like text fields.
 */
export const FIELD =
  'h-11 min-h-11 w-full min-w-0 appearance-none rounded-2xl border border-border bg-surface px-4 text-base text-fg placeholder:text-muted [&::-webkit-date-and-time-value]:text-left [&::-webkit-calendar-picker-indicator]:opacity-70';

export interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string | null;
}

export function TextField({ label, hint, error, id, ...input }: TextFieldProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label htmlFor={inputId} className="text-sm font-medium text-muted">
        {label}
      </label>
      <input
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        className={FIELD}
        {...input}
      />
      {hint ? (
        <p id={hintId} className="text-xs text-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
